import React, { createContext, useContext, useEffect, ReactNode } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../store";
import {
  login,
  logout as logoutAction,
  getProfile,
  setAuthToken,
  clearAuthError,
} from "../store";

interface AuthContextType {
  user: any | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

// Admin email addresses - you can add more admin emails here
const ADMIN_EMAILS = [
  "admin@risestrong.com",
  // Add your admin email here
];

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const loading = useSelector((state: RootState) => state.auth.loading);
  const token = useSelector((state: RootState) => state.auth.token);

  const isAdmin = user ? ADMIN_EMAILS.includes(user.email || "") : false;

  useEffect(() => {
    // Check if user is already authenticated
    const storedToken = localStorage.getItem("authToken");
    if (storedToken && !user) {
      dispatch(setAuthToken(storedToken));
      dispatch(getProfile());
    }
  }, [dispatch, user]);

  const loginHandler = async (email: string, password: string) => {
    // Clear any previous errors
    dispatch(clearAuthError());
    // Dispatch login action - the saga will handle it
    dispatch(login({ email, password }));
    // The Login component will handle errors via Redux state
  };

  const logoutHandler = async () => {
    try {
      dispatch(logoutAction());
      // Navigation will be handled by the component using this hook
    } catch (error) {
      throw error;
    }
  };

  const value = {
    user,
    loading,
    login: loginHandler,
    logout: logoutHandler,
    isAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
